import React from 'react';
import { Plus, Trash2, GripVertical, Type, FileText, Table, List, BookOpen, Palette, Upload, File } from 'lucide-react';

// Page type definitions
export const PAGE_TYPES = {
  cover: {
    id: 'cover',
    icon: BookOpen,
    labelAr: 'صفحة الغلاف',
    labelEn: 'Cover Page',
    descAr: 'عنوان في منتصف الصفحة',
    descEn: 'Title centered on page'
  },
  sections: {
    id: 'sections',
    icon: List,
    labelAr: 'صفحة الأقسام',
    labelEn: 'Sections Page',
    descAr: 'قائمة بأسماء الأقسام',
    descEn: 'List of section names'
  },
  data: {
    id: 'data',
    icon: FileText,
    labelAr: 'صفحة البيانات',
    labelEn: 'Data Page',
    descAr: 'مواصفات وبيانات المنتج',
    descEn: 'Product specifications and data'
  },
  letter: {
    id: 'letter',
    icon: Type,
    labelAr: 'صفحة الرسالة',
    labelEn: 'Letter Page',
    descAr: 'تاريخ وعنوان ونص',
    descEn: 'Date, title and text'
  },
  tables: {
    id: 'tables',
    icon: Table,
    labelAr: 'صفحة الجداول',
    labelEn: 'Tables Page',
    descAr: 'جداول قابلة للتخصيص',
    descEn: 'Customizable tables'
  },
  document: {
    id: 'document',
    icon: File,
    labelAr: 'إضافة مستند',
    labelEn: 'Add Document',
    descAr: 'رفع ملف PDF كصفحة كاملة',
    descEn: 'Upload PDF as full page'
  }
};

// Style Controls Component - reusable for all pages
const StyleControls = ({ styles, onChange, isRTL, showTitleStyle = true, showTextStyle = true }) => {
  return (
    <div className="p-3 bg-gray-50 rounded-lg border space-y-3">
      <div className="flex items-center gap-2 text-gray-600 mb-2">
        <Palette className="w-4 h-4" />
        <span className="text-sm font-medium">{isRTL ? 'التنسيق' : 'Styling'}</span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {showTitleStyle && (
          <>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                {isRTL ? 'لون العنوان' : 'Title Color'}
              </label>
              <input
                type="color"
                value={styles.titleColor || '#1A4D91'}
                onChange={(e) => onChange({ ...styles, titleColor: e.target.value })}
                className="w-full h-8 border rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                {isRTL ? 'حجم العنوان' : 'Title Size'}
              </label>
              <input
                type="number"
                min="12"
                max="72"
                value={styles.titleSize || 36}
                onChange={(e) => onChange({ ...styles, titleSize: parseInt(e.target.value) || 36 })}
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>
          </>
        )}
        
        {showTextStyle && (
          <>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                {isRTL ? 'لون النص' : 'Text Color'}
              </label>
              <input
                type="color"
                value={styles.textColor || '#333333'}
                onChange={(e) => onChange({ ...styles, textColor: e.target.value })}
                className="w-full h-8 border rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                {isRTL ? 'حجم النص' : 'Text Size'}
              </label>
              <input
                type="number"
                min="8"
                max="36"
                value={styles.textSize || 12}
                onChange={(e) => onChange({ ...styles, textSize: parseInt(e.target.value) || 12 })}
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Cover Page Component
export const CoverPageEditor = ({ page, onChange, isRTL }) => {
  const updateStyles = (newStyles) => {
    onChange({ ...page, styles: newStyles });
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg border">
      <div className="flex items-center gap-2 text-blue-600 mb-4">
        <BookOpen className="w-5 h-5" />
        <span className="font-semibold">{isRTL ? 'صفحة الغلاف' : 'Cover Page'}</span>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isRTL ? 'العنوان الرئيسي' : 'Main Title'}
        </label>
        <input
          type="text"
          value={page.title || ''}
          onChange={(e) => onChange({ ...page, title: e.target.value })}
          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-lg font-semibold"
          placeholder={isRTL ? 'أدخل العنوان الرئيسي...' : 'Enter main title...'}
          dir={isRTL ? 'rtl' : 'ltr'}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isRTL ? 'العنوان الفرعي (اختياري)' : 'Subtitle (optional)'}
        </label>
        <input
          type="text"
          value={page.subtitle || ''}
          onChange={(e) => onChange({ ...page, subtitle: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder={isRTL ? 'أدخل العنوان الفرعي...' : 'Enter subtitle...'}
          dir={isRTL ? 'rtl' : 'ltr'}
        />
      </div>
      
      {/* Style Controls */}
      <StyleControls
        styles={page.styles || { titleColor: '#1A4D91', titleSize: 36, textColor: '#666666', textSize: 18 }}
        onChange={updateStyles}
        isRTL={isRTL}
      />
    </div>
  );
};

// Sections Page Component
export const SectionsPageEditor = ({ page, onChange, isRTL }) => {
  const addSectionName = () => {
    const newSections = [...(page.sectionNames || []), ''];
    onChange({ ...page, sectionNames: newSections });
  };

  const updateSectionName = (index, value) => {
    const newSections = [...(page.sectionNames || [])];
    newSections[index] = value;
    onChange({ ...page, sectionNames: newSections });
  };

  const removeSectionName = (index) => {
    const newSections = (page.sectionNames || []).filter((_, i) => i !== index);
    onChange({ ...page, sectionNames: newSections });
  };

  const updateStyles = (newStyles) => {
    onChange({ ...page, styles: newStyles });
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg border">
      <div className="flex items-center gap-2 text-green-600 mb-4">
        <List className="w-5 h-5" />
        <span className="font-semibold">{isRTL ? 'صفحة الأقسام' : 'Sections Page'}</span>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isRTL ? 'عنوان الصفحة' : 'Page Title'}
        </label>
        <input
          type="text"
          value={page.pageTitle || ''}
          onChange={(e) => onChange({ ...page, pageTitle: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder={isRTL ? 'عنوان صفحة الأقسام...' : 'Sections page title...'}
          dir={isRTL ? 'rtl' : 'ltr'}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isRTL ? 'أسماء الأقسام' : 'Section Names'}
        </label>
        <div className="space-y-2">
          {(page.sectionNames || []).map((name, index) => (
            <div key={index} className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
              <input
                type="text"
                value={name}
                onChange={(e) => updateSectionName(index, e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder={isRTL ? `القسم ${index + 1}...` : `Section ${index + 1}...`}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              <button
                onClick={() => removeSectionName(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        
        <button
          onClick={addSectionName}
          className="mt-3 flex items-center gap-2 px-4 py-2 text-green-600 border border-green-300 rounded-lg hover:bg-green-50 w-full justify-center"
        >
          <Plus className="w-4 h-4" />
          {isRTL ? 'إضافة قسم' : 'Add Section'}
        </button>
      </div>
      
      {/* Style Controls */}
      <StyleControls
        styles={page.styles || { titleColor: '#1A4D91', titleSize: 24, textColor: '#333333', textSize: 14 }}
        onChange={updateStyles}
        isRTL={isRTL}
      />
    </div>
  );
};

// Letter Page Component
export const LetterPageEditor = ({ page, onChange, isRTL }) => {
  const addBlock = () => {
    const newBlocks = [...(page.blocks || []), { date: '', title: '', text: '' }];
    onChange({ ...page, blocks: newBlocks });
  };

  const updateBlock = (index, field, value) => {
    const newBlocks = [...(page.blocks || [])];
    newBlocks[index] = { ...newBlocks[index], [field]: value };
    onChange({ ...page, blocks: newBlocks });
  };

  const removeBlock = (index) => {
    const newBlocks = (page.blocks || []).filter((_, i) => i !== index);
    onChange({ ...page, blocks: newBlocks });
  };

  const updateStyles = (newStyles) => {
    onChange({ ...page, styles: newStyles });
  };

  // Initialize with one block if empty
  React.useEffect(() => {
    if (!page.blocks || page.blocks.length === 0) {
      onChange({ ...page, blocks: [{ date: '', title: '', text: '' }] });
    }
  }, []);

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg border">
      <div className="flex items-center gap-2 text-purple-600 mb-4">
        <Type className="w-5 h-5" />
        <span className="font-semibold">{isRTL ? 'صفحة الرسالة' : 'Letter Page'}</span>
      </div>
      
      {/* Style Controls at top */}
      <StyleControls
        styles={page.styles || { titleColor: '#1A4D91', titleSize: 16, textColor: '#333333', textSize: 11 }}
        onChange={updateStyles}
        isRTL={isRTL}
      />
      
      {(page.blocks || []).map((block, index) => (
        <div key={index} className="p-4 bg-gray-50 rounded-lg border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">
              {isRTL ? `الكتلة ${index + 1}` : `Block ${index + 1}`}
            </span>
            {(page.blocks || []).length > 1 && (
              <button
                onClick={() => removeBlock(index)}
                className="p-1 text-red-500 hover:bg-red-100 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Date Field */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {isRTL ? 'التاريخ' : 'Date'}
            </label>
            <textarea
              value={block.date || ''}
              onChange={(e) => updateBlock(index, 'date', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
              placeholder={isRTL ? 'أدخل التاريخ...' : 'Enter date...'}
              rows={2}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          
          {/* Title Field */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {isRTL ? 'العنوان' : 'Title'}
            </label>
            <textarea
              value={block.title || ''}
              onChange={(e) => updateBlock(index, 'title', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 resize-none font-semibold"
              placeholder={isRTL ? 'أدخل العنوان...' : 'Enter title...'}
              rows={2}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          
          {/* Text Field */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {isRTL ? 'النص' : 'Text'}
            </label>
            <textarea
              value={block.text || ''}
              onChange={(e) => updateBlock(index, 'text', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 resize-y min-h-[100px]"
              placeholder={isRTL ? 'أدخل النص...' : 'Enter text...'}
              rows={4}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
        </div>
      ))}
      
      <button
        onClick={addBlock}
        className="flex items-center gap-2 px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        {isRTL ? 'إضافة كتلة جديدة' : 'Add New Block'}
      </button>
    </div>
  );
};

// Tables Page Component
export const TablesPageEditor = ({ page, onChange, isRTL }) => {
  const addTable = () => {
    const newTables = [...(page.tables || []), {
      title: '',
      description: '',
      columns: 3,
      rows: 3,
      data: Array(3).fill(null).map(() => Array(3).fill('')),
      fontSize: 10,
      fontColor: '#000000',
      headerColor: '#1A4D91',
      titleColor: '#1A4D91',
      titleSize: 14,
      descColor: '#666666',
      descSize: 10
    }];
    onChange({ ...page, tables: newTables });
  };

  const updateTable = (index, field, value) => {
    const newTables = [...(page.tables || [])];
    newTables[index] = { ...newTables[index], [field]: value };
    onChange({ ...page, tables: newTables });
  };

  const updateTableSize = (index, newCols, newRows) => {
    const table = (page.tables || [])[index];
    const currentData = table.data || [];
    
    const newData = Array(newRows).fill(null).map((_, rowIdx) =>
      Array(newCols).fill(null).map((_, colIdx) =>
        currentData[rowIdx]?.[colIdx] || ''
      )
    );
    
    const newTables = [...(page.tables || [])];
    newTables[index] = { ...newTables[index], data: newData, columns: newCols, rows: newRows };
    onChange({ ...page, tables: newTables });
  };

  const updateCell = (tableIndex, rowIdx, colIdx, value) => {
    const newTables = [...(page.tables || [])];
    const newData = [...(newTables[tableIndex].data || [])];
    newData[rowIdx] = [...(newData[rowIdx] || [])];
    newData[rowIdx][colIdx] = value;
    newTables[tableIndex] = { ...newTables[tableIndex], data: newData };
    onChange({ ...page, tables: newTables });
  };

  const addColumn = (tableIndex) => {
    const table = (page.tables || [])[tableIndex];
    updateTableSize(tableIndex, (table.columns || 3) + 1, table.rows || 3);
  };

  const addRow = (tableIndex) => {
    const table = (page.tables || [])[tableIndex];
    updateTableSize(tableIndex, table.columns || 3, (table.rows || 3) + 1);
  };

  const removeTable = (index) => {
    const newTables = (page.tables || []).filter((_, i) => i !== index);
    onChange({ ...page, tables: newTables });
  };

  React.useEffect(() => {
    if (!page.tables || page.tables.length === 0) {
      addTable();
    }
  }, []);

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg border">
      <div className="flex items-center gap-2 text-orange-600 mb-4">
        <Table className="w-5 h-5" />
        <span className="font-semibold">{isRTL ? 'صفحة الجداول' : 'Tables Page'}</span>
      </div>
      
      {(page.tables || []).map((table, tableIndex) => (
        <div key={tableIndex} className="p-4 bg-gray-50 rounded-lg border space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">
              {isRTL ? `الجدول ${tableIndex + 1}` : `Table ${tableIndex + 1}`}
            </span>
            {(page.tables || []).length > 1 && (
              <button
                onClick={() => removeTable(tableIndex)}
                className="p-1 text-red-500 hover:bg-red-100 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Table Title with styling */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">
                {isRTL ? 'عنوان الجدول' : 'Table Title'}
              </label>
              <input
                type="text"
                value={table.title || ''}
                onChange={(e) => updateTable(tableIndex, 'title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder={isRTL ? 'عنوان الجدول...' : 'Table title...'}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">
                  {isRTL ? 'لون العنوان' : 'Title Color'}
                </label>
                <input
                  type="color"
                  value={table.titleColor || '#1A4D91'}
                  onChange={(e) => updateTable(tableIndex, 'titleColor', e.target.value)}
                  className="w-full h-9 border rounded cursor-pointer"
                />
              </div>
              <div className="w-20">
                <label className="block text-xs text-gray-500 mb-1">
                  {isRTL ? 'الحجم' : 'Size'}
                </label>
                <input
                  type="number"
                  min="10"
                  max="28"
                  value={table.titleSize || 14}
                  onChange={(e) => updateTable(tableIndex, 'titleSize', parseInt(e.target.value) || 14)}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
            </div>
          </div>
          
          {/* Table Description with styling */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">
                {isRTL ? 'وصف الجدول (اختياري)' : 'Description (optional)'}
              </label>
              <textarea
                value={table.description || ''}
                onChange={(e) => updateTable(tableIndex, 'description', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 resize-none"
                placeholder={isRTL ? 'وصف الجدول...' : 'Table description...'}
                rows={2}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">
                  {isRTL ? 'لون الوصف' : 'Desc Color'}
                </label>
                <input
                  type="color"
                  value={table.descColor || '#666666'}
                  onChange={(e) => updateTable(tableIndex, 'descColor', e.target.value)}
                  className="w-full h-9 border rounded cursor-pointer"
                />
              </div>
              <div className="w-20">
                <label className="block text-xs text-gray-500 mb-1">
                  {isRTL ? 'الحجم' : 'Size'}
                </label>
                <input
                  type="number"
                  min="8"
                  max="18"
                  value={table.descSize || 10}
                  onChange={(e) => updateTable(tableIndex, 'descSize', parseInt(e.target.value) || 10)}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
            </div>
          </div>
          
          {/* Table Settings */}
          <div className="flex flex-wrap gap-3 p-3 bg-white rounded border">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">{isRTL ? 'الأعمدة:' : 'Columns:'}</label>
              <input
                type="number"
                min="1"
                max="10"
                value={table.columns || 3}
                onChange={(e) => updateTableSize(tableIndex, parseInt(e.target.value) || 3, table.rows || 3)}
                className="w-14 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">{isRTL ? 'الصفوف:' : 'Rows:'}</label>
              <input
                type="number"
                min="1"
                max="50"
                value={table.rows || 3}
                onChange={(e) => updateTableSize(tableIndex, table.columns || 3, parseInt(e.target.value) || 3)}
                className="w-14 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">{isRTL ? 'حجم الخط:' : 'Font:'}</label>
              <input
                type="number"
                min="8"
                max="18"
                value={table.fontSize || 10}
                onChange={(e) => updateTable(tableIndex, 'fontSize', parseInt(e.target.value) || 10)}
                className="w-14 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">{isRTL ? 'لون الخط:' : 'Font:'}</label>
              <input
                type="color"
                value={table.fontColor || '#000000'}
                onChange={(e) => updateTable(tableIndex, 'fontColor', e.target.value)}
                className="w-8 h-8 border rounded cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">{isRTL ? 'لون الرأس:' : 'Header:'}</label>
              <input
                type="color"
                value={table.headerColor || '#1A4D91'}
                onChange={(e) => updateTable(tableIndex, 'headerColor', e.target.value)}
                className="w-8 h-8 border rounded cursor-pointer"
              />
            </div>
          </div>
          
          {/* Table Grid */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" dir={isRTL ? 'rtl' : 'ltr'}>
              <tbody>
                {(table.data || []).map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {(row || []).map((cell, colIdx) => (
                      <td key={colIdx} className="border p-0">
                        <input
                          type="text"
                          value={cell || ''}
                          onChange={(e) => updateCell(tableIndex, rowIdx, colIdx, e.target.value)}
                          className={`w-full px-2 py-1 text-sm border-0 focus:ring-2 focus:ring-orange-500 ${
                            rowIdx === 0 ? 'font-semibold bg-gray-100' : ''
                          }`}
                          style={{
                            fontSize: `${table.fontSize || 10}px`,
                            color: table.fontColor || '#000000'
                          }}
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Add Row/Column Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => addColumn(tableIndex)}
              className="flex items-center gap-1 px-3 py-1 text-sm text-orange-600 border border-orange-300 rounded hover:bg-orange-50"
            >
              <Plus className="w-3 h-3" />
              {isRTL ? 'عمود' : 'Column'}
            </button>
            <button
              onClick={() => addRow(tableIndex)}
              className="flex items-center gap-1 px-3 py-1 text-sm text-orange-600 border border-orange-300 rounded hover:bg-orange-50"
            >
              <Plus className="w-3 h-3" />
              {isRTL ? 'صف' : 'Row'}
            </button>
          </div>
        </div>
      ))}
      
      <button
        onClick={addTable}
        className="flex items-center gap-2 px-4 py-2 text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50 w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        {isRTL ? 'إضافة جدول جديد' : 'Add New Table'}
      </button>
    </div>
  );
};

// Data Page Component - Each page has its own independent data
export const DataPageEditor = ({ page, pageId, onChange, isRTL }) => {
  // Use page's own data instead of shared state
  const pageData = page || {};
  const productName = pageData.productName || '';
  const sections = pageData.sections || [
    { id: 1, title: isRTL ? 'وصف المنتج' : 'Product Description', items: [''] },
    { id: 2, title: isRTL ? 'الميزات الرئيسية' : 'Key Features', items: [''] },
  ];
  const images = pageData.images || [];
  
  // Unique ID for this page's file input
  const uniqueInputId = `image-upload-page-${pageId || Date.now()}`;

  // Update page data
  const updatePageData = (newData) => {
    onChange({ ...pageData, ...newData });
  };

  const setProductName = (name) => {
    updatePageData({ productName: name });
  };

  const setSections = (newSections) => {
    updatePageData({ sections: newSections });
  };

  const addSection = () => {
    const newId = Math.max(...sections.map(s => s.id), 0) + 1;
    setSections([...sections, { 
      id: newId, 
      title: isRTL ? 'قسم جديد' : 'New Section', 
      items: ['']
    }]);
  };

  const removeSection = (id) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const updateSectionTitle = (id, title) => {
    setSections(sections.map(s => s.id === id ? { ...s, title } : s));
  };

  const addItem = (sectionId) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, items: [...s.items, ''] } : s
    ));
  };

  const removeItem = (sectionId, itemIndex) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, items: s.items.filter((_, i) => i !== itemIndex) } : s
    ));
  };

  const updateItem = (sectionId, itemIndex, value) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { 
        ...s, 
        items: s.items.map((item, i) => i === itemIndex ? value : item) 
      } : s
    ));
  };

  // Image handling for this specific page
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newImages = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    updatePageData({ images: [...images, ...newImages] });
  };

  const removeImage = (imageId) => {
    updatePageData({ images: images.filter(img => img.id !== imageId) });
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg border">
      <div className="flex items-center gap-2 text-blue-600 mb-4">
        <FileText className="w-5 h-5" />
        <span className="font-semibold">{isRTL ? 'صفحة البيانات' : 'Data Page'}</span>
      </div>
      
      {/* Two-column layout: Text on one side, Images on other */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Text Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-700 border-b pb-2">
            {isRTL ? '📝 قسم النصوص' : '📝 Text Section'}
          </h4>
          
          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'اسم المنتج' : 'Product Name'}
            </label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={isRTL ? 'أدخل اسم المنتج...' : 'Enter product name...'}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          
          {/* Sections */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {sections.map((section) => (
              <div key={section.id} className="p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                    className="flex-1 px-2 py-1 border rounded font-medium text-sm"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                  <button
                    onClick={() => removeSection(section.id)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-2 mr-4">
                  {section.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateItem(section.id, idx, e.target.value)}
                        className="flex-1 px-2 py-1 border rounded text-sm"
                        placeholder={isRTL ? 'أدخل النص...' : 'Enter text...'}
                        dir={isRTL ? 'rtl' : 'ltr'}
                      />
                      <button
                        onClick={() => removeItem(section.id, idx)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addItem(section.id)}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    + {isRTL ? 'إضافة عنصر' : 'Add Item'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <button
            onClick={addSection}
            className="flex items-center gap-2 px-3 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 w-full justify-center text-sm"
          >
            <Plus className="w-4 h-4" />
            {isRTL ? 'إضافة قسم جديد' : 'Add New Section'}
          </button>
        </div>
        
        {/* Images Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-700 border-b pb-2">
            {isRTL ? '🖼️ قسم الصور' : '🖼️ Images Section'}
          </h4>
          
          {/* Image Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              id={uniqueInputId}
            />
            <label 
              htmlFor={uniqueInputId}
              className="cursor-pointer"
            >
              <div className="text-gray-500">
                <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">{isRTL ? 'انقر لإضافة صور' : 'Click to add images'}</p>
                <p className="text-xs text-gray-400">{isRTL ? 'PNG, JPG, WEBP' : 'PNG, JPG, WEBP'}</p>
              </div>
            </label>
          </div>
          
          {/* Images Preview - Vertical Stack */}
          {images.length > 0 && (
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {images.map((img, index) => (
                <div key={img.id} className="relative group border rounded-lg overflow-hidden">
                  <img
                    src={img.preview}
                    alt={img.name}
                    className="w-full h-32 object-contain bg-gray-50"
                  />
                  <button
                    onClick={() => removeImage(img.id)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                    {index + 1}. {img.name}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {images.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-8">
              {isRTL ? 'لا توجد صور مضافة' : 'No images added'}
            </div>
          )}
          
          <p className="text-xs text-gray-500 text-center">
            {isRTL 
              ? `${images.length} صورة مضافة لهذه الصفحة`
              : `${images.length} images added to this page`
            }
          </p>
        </div>
      </div>
    </div>
  );
};

// Document Page Editor - For uploading PDF files as full pages
export const DocumentPageEditor = ({ page, onChange, isRTL }) => {
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(page.data?.previewUrl || null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert(isRTL ? 'يرجى اختيار ملف PDF فقط' : 'Please select a PDF file only');
      return;
    }

    setUploading(true);
    
    try {
      // Create a preview URL for the PDF
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      // Convert file to base64 for storage
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result;
        onChange({
          ...page,
          data: {
            ...page.data,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            fileData: base64,
            previewUrl: url,
            uploadedAt: new Date().toISOString()
          }
        });
      };
      reader.readAsDataURL(file);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(isRTL ? 'فشل في رفع الملف' : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    onChange({
      ...page,
      data: {
        ...page.data,
        fileName: null,
        fileSize: null,
        fileType: null,
        fileData: null,
        previewUrl: null
      }
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {/* File Upload Area */}
      <div className="border-2 border-dashed rounded-lg p-6 text-center">
        {!page.data?.fileName ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileUpload}
              className="hidden"
              id="document-upload"
            />
            <label
              htmlFor="document-upload"
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Upload className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-700">
                  {isRTL ? 'اضغط لرفع ملف PDF' : 'Click to upload PDF file'}
                </p>
                <p className="text-sm text-gray-500">
                  {isRTL ? 'سيظهر بكامل الصفحة بدون رأسية أو تذييل' : 'Will appear as full page without header/footer'}
                </p>
              </div>
              {uploading && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>{isRTL ? 'جاري الرفع...' : 'Uploading...'}</span>
                </div>
              )}
            </label>
          </>
        ) : (
          <div className="space-y-4">
            {/* File Info */}
            <div className="flex items-center justify-center gap-3 p-4 bg-green-50 rounded-lg">
              <File className="w-10 h-10 text-red-500" />
              <div className="text-right">
                <p className="font-medium text-gray-800">{page.data.fileName}</p>
                <p className="text-sm text-gray-500">{formatFileSize(page.data.fileSize)}</p>
              </div>
            </div>

            {/* Page Orientation Selection */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {isRTL ? 'اتجاه الصفحة:' : 'Page Orientation:'}
              </label>
              <div className="flex gap-4 justify-center">
                <label className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  (page.data?.orientation || 'portrait') === 'portrait' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="orientation"
                    value="portrait"
                    checked={(page.data?.orientation || 'portrait') === 'portrait'}
                    onChange={() => onChange({ ...page, data: { ...page.data, orientation: 'portrait' } })}
                    className="w-4 h-4"
                  />
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-8 border-2 border-current rounded"></div>
                    <span className="text-sm font-medium">{isRTL ? 'عمودي' : 'Portrait'}</span>
                  </div>
                </label>
                <label className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  page.data?.orientation === 'landscape' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="orientation"
                    value="landscape"
                    checked={page.data?.orientation === 'landscape'}
                    onChange={() => onChange({ ...page, data: { ...page.data, orientation: 'landscape' } })}
                    className="w-4 h-4"
                  />
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-6 border-2 border-current rounded"></div>
                    <span className="text-sm font-medium">{isRTL ? 'عرضي' : 'Landscape'}</span>
                  </div>
                </label>
              </div>
            </div>

            {/* PDF Preview */}
            {previewUrl && (
              <div className="border rounded-lg overflow-hidden bg-gray-100">
                <iframe
                  src={previewUrl}
                  className="w-full h-96"
                  title="PDF Preview"
                />
              </div>
            )}

            {/* Remove Button */}
            <button
              onClick={handleRemoveFile}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors mx-auto"
            >
              <Trash2 className="w-4 h-4" />
              {isRTL ? 'إزالة الملف' : 'Remove File'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Page Selector Component
export const PageTypeSelector = ({ onAdd, isRTL }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gray-50 rounded-lg">
      {Object.values(PAGE_TYPES).map((type) => {
        const Icon = type.icon;
        return (
          <button
            key={type.id}
            onClick={() => onAdd(type.id)}
            className="flex flex-col items-center gap-2 p-4 bg-white border rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
          >
            <Icon className="w-6 h-6 text-gray-600" />
            <span className="text-sm font-medium text-center">
              {isRTL ? type.labelAr : type.labelEn}
            </span>
            <span className="text-xs text-gray-500 text-center">
              {isRTL ? type.descAr : type.descEn}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default {
  PAGE_TYPES,
  CoverPageEditor,
  SectionsPageEditor,
  LetterPageEditor,
  TablesPageEditor,
  DataPageEditor,
  DocumentPageEditor,
  PageTypeSelector
};
